import { useState } from "react";

import { Select } from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { BankSpecificStatus } from "types";

const STATUSES: BankSpecificStatus[] = [
  "IN_PREPARATION",
  "IN_PROGRESS",
  "ADDITIONAL_INFO_REQUIRED",
  "APPROVED",
  "SIGNING",
  "CONTRACT_VERIFICATION",
  "SIGNED_COMPLETE",
  "REJECTED",
  "SIGNING_SUSPENDED",
];

const STATUS_COLORS: Record<BankSpecificStatus, string> = {
  IN_PREPARATION: "#636A79",
  IN_PROGRESS: "#B046AC",
  ADDITIONAL_INFO_REQUIRED: "#737ADF",
  APPROVED: "#60B046",
  SIGNING: "#737ADF",
  CONTRACT_VERIFICATION: "#737ADF",
  SIGNED_COMPLETE: "#60B046",
  REJECTED: "#D75060",
  SIGNING_SUSPENDED: "#D75060",
};

interface StatusBadgeProps {
  status?: BankSpecificStatus;
  statuses?: BankSpecificStatus[];
  onChange?: (status: BankSpecificStatus) => void;
  disabled?: boolean;
  loading?: boolean;
}

export const StatusBadge = ({ status, statuses, onChange, disabled, loading }: StatusBadgeProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const statusColor = status ? STATUS_COLORS[status] : "#DD992D";
  const statusBackgroundColor = status ? `${statusColor}1A` : "transparent";

  const availableStatuses = statuses ?? STATUSES;

  const statusOptions = [
    ...(status && !availableStatuses.includes(status)
      ? [{ value: status, label: t(`bankSelection.status.${status}`), disabled: true }]
      : []),
    ...availableStatuses.map((s) => ({ value: s, label: t(`bankSelection.status.${s}`) })),
  ];

  return (
    <Select
      size="xs"
      value={status || null}
      onChange={(value) => value && onChange?.(value as BankSpecificStatus)}
      data={statusOptions}
      renderOption={({ option }) => {
        const optionColor = STATUS_COLORS[option.value as BankSpecificStatus];

        return (
          <span
            style={{
              display: "block",
              padding: "4px 8px",
              borderRadius: 4,
              backgroundColor: `${optionColor}1A`,
              color: optionColor,
            }}
          >
            {option.label}
          </span>
        );
      }}
      placeholder={t("bankSelection.status.label")}
      disabled={disabled || loading}
      allowDeselect={false}
      withCheckIcon={false}
      rightSection={
        isOpen ? (
          <IconChevronUp size={14} color={statusColor} />
        ) : (
          <IconChevronDown size={14} color={statusColor} />
        )
      }
      onDropdownOpen={() => setIsOpen(true)}
      onDropdownClose={() => setIsOpen(false)}
      styles={{
        input: {
          width: 200,
          height: 32,
          minHeight: 32,
          border: `1px solid ${statusColor}`,
          color: statusColor,
          fontWeight: 500,
          fontSize: 13,
          borderRadius: 6,
          backgroundColor: statusBackgroundColor,
          paddingRight: 24,
          paddingLeft: 10,
        },
        dropdown: {
          minWidth: 220,
        },
      }}
    />
  );
};
